/**
 * Patient Management – CareArena
 *
 * Features:
 *  • List all patients for the authenticated user (from Supabase)
 *  • Add single patient via modal form
 *  • Bulk import from Excel / CSV → stored in Supabase
 *  • Inline edit (click row → edit form)
 *  • Delete patients
 *  • Search / filter by name, phone, diagnosis, status
 *  • Real-time subscription: table updates live when data changes
 *  • Multi-user safe: Supabase RLS ensures each user only sees their data
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  IconAdd,
  IconClose,
  IconDelete,
  IconDownload,
  IconEdit,
  IconFailed,
  IconRefresh,
  IconSave,
  IconSearch,
  IconSuccess,
  IconUpload,
} from '../components/icons';
import {
  IconButton,
  Select,
  StatusPill,
} from '../components/ui';
import * as XLSX from 'xlsx';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import Button from '../components/Button';

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Normalize phone number to E.164 Ghana format (233XXXXXXXXX)
const normalizePhone = (raw) => {
  let cleaned = String(raw).replace(/\D/g, '');
  if (cleaned.startsWith('0') && cleaned.length >= 9) {
    cleaned = '233' + cleaned.substring(1);
  } else if (!cleaned.startsWith('233') && cleaned.length === 9) {
    cleaned = '233' + cleaned;
  }
  return cleaned;
};
const engagementColor = (pct) => {
  if (pct >= 80) return 'text-green-700 bg-green-100';
  if (pct >= 50) return 'text-yellow-700 bg-yellow-100';
  return 'text-red-700 bg-red-100';
};

// Patient status is stored lowercase ('active' / 'inactive') in the DB.
// Older rows may still be capitalised, so always compare case-insensitively.
const isActive = (s) => String(s || 'active').toLowerCase() === 'active';
const statusLabel = (s) => (isActive(s) ? 'Active' : 'Inactive');
const statusTone = (s) => (isActive(s) ? 'good' : 'neutral');

const emptyForm = () => ({
  name: '',
  phone: '',
  email: '',
  diagnosis: '',
  hospital: '',
  preferred_language: 'en',
  notes: '',
  status: 'active',
});

// ─── Component ────────────────────────────────────────────────────────────────
const PatientManagement = () => {
  const { user } = useAuth();

  // ── Data state ──────────────────────────────────────────────────────────────
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ── UI state ────────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDiagnosis, setFilterDiagnosis] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [selectedIds, setSelectedIds] = useState([]);

  // ── Add / Edit modal ────────────────────────────────────────────────────────
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // ── Import ───────────────────────────────────────────────────────────────────
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // Load patients from REST API
  // ─────────────────────────────────────────────────────────────────────────────
  const loadPatients = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/patients', { params: { user_id: user.id } });
      setPatients(data?.patients ?? []);
    } catch (err) {
      setError('Failed to load patients: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadPatients();
  }, [loadPatients]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Filtering
  // ─────────────────────────────────────────────────────────────────────────────
  const diagnoses = ['All', ...new Set(patients.map((p) => p.diagnosis).filter(Boolean))];

  const filtered = patients.filter((p) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      !q ||
      p.name?.toLowerCase().includes(q) ||
      p.phone?.toLowerCase().includes(q) ||
      p.email?.toLowerCase().includes(q);
    const matchesDiagnosis = filterDiagnosis === 'All' || p.diagnosis === filterDiagnosis;
    // Compare status case-insensitively so legacy "Active" rows match
    // the lowercase canonical value the dropdown sends.
    const matchesStatus =
      filterStatus === 'All' ||
      String(p.status || 'active').toLowerCase() === String(filterStatus).toLowerCase();
    return matchesSearch && matchesDiagnosis && matchesStatus;
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // Selection
  // ─────────────────────────────────────────────────────────────────────────────
  const toggleSelect = (id) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const toggleAll = () =>
    setSelectedIds(selectedIds.length === filtered.length ? [] : filtered.map((p) => p.id));

  // ─────────────────────────────────────────────────────────────────────────────
  // Add / Edit
  // ─────────────────────────────────────────────────────────────────────────────
  const openAdd = () => {
    setForm(emptyForm());
    setEditingId(null);
    setFormError('');
    setShowAddModal(true);
  };

  const openEdit = (patient) => {
    setForm({
      name: patient.name || '',
      phone: patient.phone || '',
      email: patient.email || '',
      diagnosis: patient.diagnosis || '',
      hospital: patient.hospital || '',
      preferred_language: patient.preferred_language || 'en',
      notes: patient.notes || '',
      status: String(patient.status || 'active').toLowerCase(),
    });
    setEditingId(patient.id);
    setFormError('');
    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingId(null);
    setFormError('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!form.name.trim()) return setFormError('Patient name is required.');
    if (!form.phone.trim()) return setFormError('Phone number is required.');

    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/patients/${editingId}`, { ...form, updated_at: new Date().toISOString() });
      } else {
        await api.post('/patients', { ...form, phone: normalizePhone(form.phone), user_id: user.id });
      }
      closeModal();
      await loadPatients();
    } catch (err) {
      setFormError(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Delete
  // ─────────────────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this patient? This cannot be undone.')) return;
    try {
      await api.delete(`/patients/${id}`);
      await loadPatients();
    } catch (err) {
      setError('Delete failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected patient(s)?`)) return;
    try {
      await Promise.all(selectedIds.map((id) => api.delete(`/patients/${id}`)));
      setSelectedIds([]);
      await loadPatients();
    } catch (err) {
      setError('Bulk delete failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Bulk Import (Excel / CSV)
  // ─────────────────────────────────────────────────────────────────────────────
  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setImporting(true);
    setImportMsg(null);

    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws);

      if (!rows.length) throw new Error('File is empty or has no data rows.');

      // Normalise column names
      const normalised = rows.map((r) => {
        const lower = {};
        Object.entries(r).forEach(([k, v]) => (lower[k.toLowerCase().trim()] = v));
        return lower;
      });

      const missing = normalised.filter((r) => !r.name || !r.phone);
      if (missing.length === normalised.length)
        throw new Error('Columns "name" and "phone" are required.');

      const records = normalised
        .filter((r) => r.name && r.phone)
        .map((r) => ({
          user_id: user.id,
          name: String(r.name).trim(),
          phone: normalizePhone(r.phone),
          email: String(r.email || '').trim(),
          diagnosis: String(r.diagnosis || r.condition || '').trim(),
          hospital: String(r.hospital || '').trim(),
          preferred_language: String(r.language || r.preferred_language || 'en').trim(),
          notes: String(r.notes || '').trim(),
          status: String(r.status || 'active').toLowerCase(),
        }));

      await api.post('/patients/bulk', { patients: records });

      setImportMsg({ ok: true, text: `Imported ${records.length} patients successfully.` });
      await loadPatients();
    } catch (err) {
      setImportMsg({ ok: false, text: `Import failed: ${err.message}` });
    } finally {
      setImporting(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Export
  // ─────────────────────────────────────────────────────────────────────────────
  const handleExport = () => {
    const exportData = filtered.map(({ user_id, id, created_at, updated_at, ...rest }) => rest);
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Patients');
    XLSX.writeFile(wb, 'carearena_patients.xlsx');
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Patient Management</h1>
          <p className="text-sm text-gray-500 mt-1">
            {patients.length} patient{patients.length !== 1 ? 's' : ''} in your database
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadPatients}
            title="Refresh"
            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-500"
          >
            <IconRefresh className="h-4 w-4" />
          </button>
          <button
            onClick={handleExport}
            title="Export to Excel"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm text-gray-600"
          >
            <IconDownload className="h-4 w-4" /> Export
          </button>
          {/* File import input (hidden) */}
          <label className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm text-gray-600 cursor-pointer">
            <IconUpload className="h-4 w-4" />
            {importing ? 'Importing…' : 'Import'}
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleImport}
              className="hidden"
            />
          </label>
          <Button onClick={openAdd} className="flex items-center gap-1.5">
            <IconAdd className="h-4 w-4" /> Add Patient
          </Button>
        </div>
      </div>

      {/* Import message */}
      {importMsg && (
        <div className={`mb-4 p-3 rounded-lg text-sm flex items-start gap-2 ${importMsg.ok ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {importMsg.ok
            ? <IconSuccess className="h-4 w-4 mt-0.5 shrink-0" />
            : <IconFailed className="h-4 w-4 mt-0.5 shrink-0" />}
          {importMsg.text}
          <button
            onClick={() => setImportMsg(null)}
            className="ml-auto opacity-60 hover:opacity-100"
            aria-label="Dismiss"
          >
            <IconClose className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex justify-between">
          {error}
          <button onClick={() => setError('')} aria-label="Dismiss" className="opacity-60 hover:opacity-100">
            <IconClose className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-48">
          <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or phone…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-4 py-2 w-full border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <Select
          value={filterDiagnosis}
          onChange={(e) => setFilterDiagnosis(e.target.value)}
          containerClassName="w-full sm:w-56"
          aria-label="Filter by diagnosis"
        >
          {diagnoses.map((d) => <option key={d}>{d}</option>)}
        </Select>
        <Select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          containerClassName="w-full sm:w-36"
          aria-label="Filter by status"
        >
          {['All', 'Active', 'Inactive'].map((s) => <option key={s}>{s}</option>)}
        </Select>
        {selectedIds.length > 0 && (
          <button
            onClick={handleDeleteSelected}
            className="px-3 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg text-sm hover:bg-red-100"
          >
            Delete {selectedIds.length} selected
          </button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-400">
          <div className="h-6 w-6 rounded-full border-2 border-primary-500 border-t-transparent animate-spin mr-3" />
          Loading patients…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <p className="text-lg font-medium">No patients found</p>
          <p className="text-sm mt-1">Add patients or adjust your filters.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedIds.length === filtered.length && filtered.length > 0}
                    onChange={toggleAll}
                    className="rounded"
                  />
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Name</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Phone</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Diagnosis</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Hospital</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Added</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((p) => (
                <tr key={p.id} className="hover:bg-gray-50 transition">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(p.id)}
                      onChange={() => toggleSelect(p.id)}
                      className="rounded"
                    />
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-900">{p.name}</td>
                  <td className="px-4 py-3 text-gray-600">{p.phone}</td>
                  <td className="px-4 py-3 text-gray-600">{p.diagnosis || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{p.hospital || '—'}</td>
                  <td className="px-4 py-3">
                    <StatusPill tone={statusTone(p.status)}>{statusLabel(p.status)}</StatusPill>
                  </td>
                  <td className="px-4 py-3 text-gray-500">
                    {p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(p)} />
                      <IconButton
                        icon={IconDelete}
                        label="Delete"
                        tone="danger"
                        onClick={() => handleDelete(p.id)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
            Showing {filtered.length} of {patients.length} patients
          </div>
        </div>
      )}

      {/* ── Add / Edit Modal ───────────────────────────────────────────────────── */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingId ? 'Edit Patient' : 'Add Patient'}
              </h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <IconClose className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSave} className="px-6 py-5 space-y-4">
              {formError && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                  {formError}
                </div>
              )}

              {[
                { id: 'name', label: 'Full Name *', type: 'text', placeholder: 'Akua Mensah' },
                { id: 'phone', label: 'Phone Number *', type: 'tel', placeholder: '+233 242 000 000' },
                { id: 'email', label: 'Email (optional)', type: 'email', placeholder: 'akua@example.com' },
                { id: 'diagnosis', label: 'Diagnosis / Condition', type: 'text', placeholder: 'Preeclampsia' },
                { id: 'hospital', label: 'Hospital / Clinic', type: 'text', placeholder: 'Korle Bu Teaching Hospital' },
              ].map(({ id, label, type, placeholder }) => (
                <div key={id}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <input
                    type={type}
                    value={form[id]}
                    onChange={(e) => setForm((f) => ({ ...f, [id]: e.target.value }))}
                    placeholder={placeholder}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary-500"
                    disabled={saving}
                  />
                </div>
              ))}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
                  <select
                    value={form.preferred_language}
                    onChange={(e) => setForm((f) => ({ ...f, preferred_language: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-primary-500"
                    disabled={saving}
                  >
                    <option value="en">English</option>
                    <option value="tw">Twi</option>
                    <option value="ga">Ga</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-primary-500"
                    disabled={saving}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Any additional notes…"
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                  disabled={saving}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {saving ? (
                    <>
                      <div className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <IconSave className="h-4 w-4" />
                      {editingId ? 'Save Changes' : 'Add Patient'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientManagement;
